import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { Card } from '@/shared/components/ui/card';
import { Badge } from '@/shared/components/ui/badge';
import { HighlightedText } from '@/shared/components/ui/HighlightedText';
import { ArrowUpRight, ChevronDown, ChevronUp, Compass } from 'lucide-react';
import { changes, ChangeRelease } from '@/shared/config/changelog-data';
import { changelogIcons } from '@/shared/config/changelog-icons';
import { RippleButton } from '@/shared/components/AnimateUi/RippleButton';
import { useOnboarding } from '@/features/onboarding/providers/OnboardingProvider';

export const Route = createFileRoute('/changelog')({
  component: Changelog,
});

interface ReleaseItemProps {
  release: ChangeRelease;
  showLine: boolean;
}

function ReleaseItem({ release, showLine }: ReleaseItemProps) {
  const navigate = useNavigate();

  return (
    <div className="relative pl-8 sm:pl-10">
      {/* Timeline line - extends further down for connection */}
      {showLine && (
        <div className="absolute left-3 sm:left-4 top-8 bottom-0 w-px bg-gray-200 dark:bg-slate-800 -mb-8" />
      )}

      {/* Timeline dot */}
      <div className="absolute left-0 sm:left-1 top-1.5 w-6 h-6 sm:w-6 sm:h-6 rounded-full border-4 border-white dark:border-slate-950 bg-primary shadow-sm z-10" />

      <div className="space-y-4 pb-8">
        <div className="flex flex-col sm:flex-row sm:items-baseline gap-2 sm:gap-4">
          <h2 className="text-xl font-bold text-gray-900 dark:text-slate-100">{release.version}</h2>
          <span className="text-sm font-medium text-gray-500 dark:text-slate-400">
            {release.date}
          </span>
        </div>

        <Card className="border-none shadow-md bg-white dark:bg-slate-900 overflow-hidden p-4">
          <div className="px-2 space-y-6">
            {release.features.map((feature, fIndex) => {
              const Icon = changelogIcons[feature.icon];
              return (
                <div key={fIndex} className="flex gap-3">
                  <div className="mt-0.5 p-1.5 rounded-lg bg-primary/10 text-primary shrink-0 h-fit">
                    <Icon className="w-6 h-6" />
                  </div>
                  <div className="flex-1 flex items-center justify-between gap-4">
                    <div className="space-y-1">
                      <div className="flex flex-wrap items-center gap-1">
                        <h3 className="text-base font-semibold text-gray-900 dark:text-slate-100">
                          {feature.title}
                        </h3>
                        <Badge
                          variant={feature.type === 'new' ? 'default' : 'secondary'}
                          className="text-[10px] px-1.5 py-0 h-4 uppercase tracking-wider font-medium"
                        >
                          {feature.type === 'new' ? 'New' : 'Update'}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-slate-400 leading-relaxed">
                        <HighlightedText text={feature.description} />
                      </p>
                    </div>
                    {feature.link && (
                      <RippleButton
                        variant="outline"
                        size="sm"
                        onClick={() => navigate({ to: feature.link })}
                      >
                        Go
                        <ArrowUpRight className="w-3 h-3" />
                      </RippleButton>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      </div>
    </div>
  );
}

function Changelog() {
  const { startTour } = useOnboarding();
  const [showAll, setShowAll] = useState(false);
  const INITIAL_COUNT = 3;

  const visibleChanges = showAll ? changes : changes.slice(0, INITIAL_COUNT);
  const hasHiddenChanges = changes.length > INITIAL_COUNT;

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto space-y-8 sm:bg-white/60 sm:dark:bg-slate-800/40 sm:rounded-2xl p-0 sm:p-8 sm:border sm:border-transparent sm:dark:border-slate-700/50">
        <div className="text-center space-y-3">
          <h1 className="text-3xl sm:text-4xl font-serif font-bold text-gray-900 dark:text-slate-100 tracking-tight">
            What&apos;s New
          </h1>
          <p className="text-lg text-gray-600 dark:text-slate-400 max-w-2xl mx-auto">
            Latest updates, features, and improvements to make your RobinHoot experience better.
          </p>
          <RippleButton variant="outline" onClick={startTour}>
            <Compass className="w-4 h-4" />
            Take the tour
          </RippleButton>
        </div>

        <div className="space-y-0 relative">
          {visibleChanges.map((release, index) => (
            <ReleaseItem
              key={release.version}
              release={release}
              showLine={index !== visibleChanges.length - 1 || (hasHiddenChanges && !showAll)}
            />
          ))}
        </div>

        {hasHiddenChanges && (
          <div className="flex justify-center pt-4 relative z-10">
            <RippleButton
              variant="outline"
              size="lg"
              onClick={() => setShowAll(!showAll)}
              className="gap-2 bg-white dark:bg-slate-900 shadow-sm hover:bg-gray-50 dark:hover:bg-slate-700 z-20"
            >
              {showAll ? (
                <>
                  Show Less <ChevronUp className="w-4 h-4" />
                </>
              ) : (
                <>
                  View Older Releases <ChevronDown className="w-4 h-4" />
                </>
              )}
            </RippleButton>
          </div>
        )}
      </div>
    </div>
  );
}
